import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { endpoints } from '@/api/endpoints';
import { ApiError, getToken, setToken as persistToken } from '@/api/client';
import type { Self } from '@/api/types';

/**
 * COMMERCIAL LAYER epic (ADR-075 Part C). Unlike apps/ops (a token-paste
 * screen over device/office bearer tokens - there is no password login on
 * that boundary), the seller portal has a REAL email+password login: an
 * unsupervised external seller needs a secret only they know
 * (packages/foundation/src/crypto/password.ts, scrypt). This is the ONE
 * place the session token is stored (localStorage, separate key from
 * apps/ops) or read for every subsequent request.
 *
 * `self` is fetched once per token and cached in memory. There is no
 * permission matrix here - every seller sees the same screens; the only
 * thing that varies between sellers is which rows the API returns
 * (`ownerIds`-scoped server-side, ADR-075 §Part B.4) - so unlike
 * apps/ops's AuthContext there is no `hasPermission()`.
 */

type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; self: Self }
  | { status: 'error'; error: unknown };

type AuthContextValue = {
  readonly state: AuthState;
  readonly login: (email: string, password: string) => Promise<void>;
  readonly logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const loadSelf = useCallback(async (): Promise<void> => {
    const token = getToken();
    if (!token) {
      setState({ status: 'anonymous' });
      return;
    }
    try {
      const self = await endpoints.self();
      setState({ status: 'authenticated', self });
    } catch (err) {
      // An expired/revoked/malformed session: fall back to the login screen
      // rather than an error page - a dead session is expected and
      // recoverable, not a bug.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        persistToken(null);
        setState({ status: 'anonymous' });
      } else {
        setState({ status: 'error', error: err });
      }
    }
  }, []);

  useEffect(() => {
    void loadSelf();
  }, [loadSelf]);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const { sessionToken } = await endpoints.login(email, password);
      persistToken(sessionToken);
      await loadSelf();
    },
    [loadSelf],
  );

  const logout = useCallback((): void => {
    // Best-effort revoke server-side; the client always drops its own copy
    // regardless of whether the network call succeeds (a dead network
    // should never trap a seller in a "still logged in" UI).
    void endpoints.logout().catch(() => undefined);
    persistToken(null);
    setState({ status: 'anonymous' });
  }, []);

  const value = useMemo<AuthContextValue>(() => ({ state, login, logout }), [state, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() called outside <AuthProvider>');
  return ctx;
}
